import { LinkContentFragment } from "../../graphql/graphql.ts";
import { LocaleConfig } from "../../utils/GlobalContext.ts";
import { formatAnchorLinkId } from "../../utils/formatAnchorLinkId.ts";

export type CmsLinkProps = {
  ariaLabel?: string;
  children: string;
  href: string;
  download?: boolean;
};

export function linkTransformer({
  link,
  localeConfig,
}: {
  link?: LinkContentFragment | null;
  localeConfig: LocaleConfig;
}): CmsLinkProps {
  const emptyLink = {
    children: "",
    href: "#",
    target: "_self",
    ariaLabel: "",
  };

  if (!link) {
    return emptyLink;
  }

  if (link.file?.url) {
    return {
      children: link.label ?? "",
      href: link.file.url,
      target: "_blank",
      download: true,
    } as CmsLinkProps;
  }

  // Prefer the editor-defined `sectionUrl` (when the referenced component
  // exposes one via its inline fragment in `LinkContent`) over the auto-
  // generated hash. Falls back to `formatAnchorLinkId` so existing references
  // without `sectionUrl` keep working unchanged.
  const referencedSectionUrl =
    link.componentReference &&
    "sectionUrl" in link.componentReference &&
    link.componentReference.sectionUrl
      ? link.componentReference.sectionUrl
      : null;

  const componentReference = link.componentReference
    ? `#${referencedSectionUrl ?? formatAnchorLinkId(link.componentReference.sys.id)}`
    : "";

  // A linked CMS page contributes a slug path segment. The homepage slug "/"
  // contributes an empty segment so the rendered URL stays "/<locale>" (without
  // a trailing slash before any optional `#section` hash).
  const hasPageReference = Boolean(link.pageReference?.slug);
  const pageSlugPath =
    link.pageReference?.slug && link.pageReference.slug !== "/"
      ? `/${link.pageReference.slug}`
      : "";

  return {
    children: link.label ?? "",
    href: hasPageReference
      ? `/${localeConfig?.currentLocale.toLowerCase()}${pageSlugPath}${componentReference}`
      : componentReference
        ? componentReference
        : (link.url ?? ""),
    target:
      hasPageReference || Boolean(componentReference) ? "_self" : "_blank",
  } as CmsLinkProps;
}
